import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useParams, useNavigate } from 'react-router-dom'
import { 
  ArrowLeftIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ClockIcon
} from '@heroicons/react/24/outline'
import { supabase } from '../config/supabase'
import toast from 'react-hot-toast'
import CustomerLoader from '../components/customer/CustomerLoader'

const ACTION_GREEN = '#00C853'
const BRAND_GREEN = '#00E676'

const TableBooking = () => {
  const { restaurantId } = useParams()
  const navigate = useNavigate()
  const [restaurant, setRestaurant] = useState(null)
  const [loading, setLoading] = useState(true)
  
  // Booking state
  const [numberOfGuests, setNumberOfGuests] = useState(2)
  const [selectedDate, setSelectedDate] = useState(null)
  const [selectedTime, setSelectedTime] = useState(null)
  const [selectedOffer, setSelectedOffer] = useState(null)
  const [showAllSlots, setShowAllSlots] = useState(false)
  const [selectedMealPeriod, setSelectedMealPeriod] = useState(null)
  const [expandedMealPeriod, setExpandedMealPeriod] = useState(null)
  
  // Generate next 5 days
  const generateDates = () => {
    const dates = []
    const today = new Date()
    
    for (let i = 0; i < 5; i++) {
      const date = new Date(today)
      date.setDate(today.getDate() + i)
      dates.push({
        date: date,
        day: date.toLocaleDateString('en-US', { weekday: 'short' }),
        dayNum: date.getDate(),
        month: date.toLocaleDateString('en-US', { month: 'short' }),
        discount: i === 0 ? '30% off' : '50% off',
        isToday: i === 0
      })
    }
    return dates
  }

  const dates = generateDates()

  // Define all meal periods with their time slots
  const allMealPeriods = {
    breakfast: {
      name: 'Breakfast',
      timeRange: '07:00 AM to 11:00 AM',
      icon: '🌅',
      slots: [
        { time: '07:00 AM', discount: '30% off', available: true },
        { time: '07:30 AM', discount: '30% off', available: true },
        { time: '08:00 AM', discount: '30% off', available: true },
        { time: '08:30 AM', discount: '30% off', available: true },
        { time: '09:00 AM', discount: '30% off', available: true },
        { time: '09:30 AM', discount: '30% off', available: true },
        { time: '10:00 AM', discount: '30% off', available: true },
        { time: '10:30 AM', discount: '30% off', available: true },
      ]
    },
    lunch: {
      name: 'Lunch',
      timeRange: '12:00 PM to 03:30 PM',
      icon: '☀️',
      slots: [
        { time: '12:00 PM', discount: '40% off', available: true },
        { time: '12:30 PM', discount: '40% off', available: true },
        { time: '01:00 PM', discount: '40% off', available: true },
        { time: '01:30 PM', discount: '40% off', available: true },
        { time: '02:00 PM', discount: '40% off', available: true },
        { time: '02:30 PM', discount: '40% off', available: true },
        { time: '03:00 PM', discount: '40% off', available: true },
      ]
    },
    dinner: {
      name: 'Dinner',
      timeRange: '05:30 PM to 12:30 AM',
      icon: '🌙',
      slots: [
        { time: '05:30 PM', discount: '30% off', available: true },
        { time: '05:45 PM', discount: '30% off', available: true },
        { time: '06:00 PM', discount: '30% off', available: true },
        { time: '06:15 PM', discount: '30% off', available: true },
        { time: '06:30 PM', discount: '30% off', available: true },
        { time: '06:45 PM', discount: '30% off', available: true },
        { time: '07:00 PM', discount: '30% off', available: true },
        { time: '07:15 PM', discount: '30% off', available: true },
        { time: '07:30 PM', discount: '30% off', available: true },
        { time: '07:45 PM', discount: '30% off', available: true },
        { time: '08:00 PM', discount: '30% off', available: true },
        { time: '08:15 PM', discount: '30% off', available: true },
        { time: '08:30 PM', discount: '30% off', available: true },
        { time: '08:45 PM', discount: '30% off', available: true },
        { time: '09:00 PM', discount: '30% off', available: true },
        { time: '09:30 PM', discount: '30% off', available: true },
        { time: '10:00 PM', discount: '30% off', available: true },
        { time: '10:30 PM', discount: '30% off', available: true },
        { time: '11:00 PM', discount: '30% off', available: true },
        { time: '11:30 PM', discount: '30% off', available: true },
        { time: '12:00 AM', discount: '30% off', available: true },
      ]
    }
  }

  // Get available meal periods based on selected date
  const getAvailableMealPeriods = () => {
    if (!selectedDate) return []

    const now = new Date()
    const currentHour = now.getHours()
    const currentMinute = now.getMinutes()
    const currentTimeInMinutes = currentHour * 60 + currentMinute

    // If selected date is today, filter based on current time
    if (selectedDate.isToday) {
      const availablePeriods = []

      // Breakfast: 7:00 AM - 11:00 AM (420 - 660 minutes)
      if (currentTimeInMinutes < 660) {
        availablePeriods.push('breakfast')
      }

      // Lunch: 12:00 PM - 3:30 PM (720 - 930 minutes)
      if (currentTimeInMinutes < 930) {
        availablePeriods.push('lunch')
      }

      // Dinner: 5:30 PM - 12:30 AM (1050 - 1470 minutes, wraps to next day)
      // Always show dinner if current time is before midnight
      availablePeriods.push('dinner')

      return availablePeriods
    }

    // For future dates, show all meal periods
    return ['breakfast', 'lunch', 'dinner']
  }

  // Filter time slots based on current time for today
  const getFilteredTimeSlots = (mealPeriod) => {
    const slots = allMealPeriods[mealPeriod]?.slots || []
    
    if (!selectedDate?.isToday) {
      return slots
    }

    const now = new Date()
    const currentHour = now.getHours()
    const currentMinute = now.getMinutes()

    return slots.filter(slot => {
      const [time, period] = slot.time.split(' ')
      const [hours, minutes] = time.split(':').map(Number)
      
      let slotHour = hours
      if (period === 'PM' && hours !== 12) {
        slotHour += 12
      } else if (period === 'AM' && hours === 12) {
        slotHour = 0
      }

      const slotTimeInMinutes = slotHour * 60 + minutes
      const currentTimeInMinutes = currentHour * 60 + currentMinute

      // Show slots that are at least 30 minutes from now
      return slotTimeInMinutes > currentTimeInMinutes + 30
    })
  }

  const availableMealPeriods = getAvailableMealPeriods()
  
  // Auto-select first available meal period when date changes
  useEffect(() => {
    if (selectedDate && availableMealPeriods.length > 0) {
      const firstPeriod = availableMealPeriods[0]
      setSelectedMealPeriod(firstPeriod)
      setExpandedMealPeriod(firstPeriod)
    }
  }, [selectedDate])

  const currentMealPeriodSlots = selectedMealPeriod ? getFilteredTimeSlots(selectedMealPeriod) : []
  const visibleSlots = showAllSlots ? currentMealPeriodSlots : currentMealPeriodSlots.slice(0, 9)

  useEffect(() => {
    if (restaurantId) {
      fetchRestaurantDetails()
    }
    // Set default date to today
    setSelectedDate(dates[0])
  }, [restaurantId])

  const fetchRestaurantDetails = async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('users')
        .select('id, restaurant_name')
        .eq('id', restaurantId)
        .single()

      if (error) throw error
      setRestaurant(data)
    } catch (error) {
      console.error('Error fetching restaurant:', error)
      toast.error('Failed to load restaurant details')
    } finally {
      setLoading(false)
    }
  }

  const handleProceed = async () => {
    if (!selectedDate || !selectedTime) {
      toast.error('Please select date and time')
      return
    }

    try {
      // Create booking
      const bookingData = {
        restaurant_id: restaurantId,
        number_of_guests: numberOfGuests,
        booking_date: selectedDate.date.toISOString().split('T')[0],
        booking_time: selectedTime.time,
        meal_period: 'dinner',
        status: 'pending',
        offer_applied: selectedOffer,
        discount_percentage: selectedOffer ? parseFloat(selectedOffer.split(' ')[1]) : 0,
        booking_source: 'web'
      }

      const { data, error } = await supabase
        .from('table_bookings')
        .insert([bookingData])
        .select()
        .single()

      if (error) throw error

      toast.success('Booking created successfully!')
      navigate(`/booking-confirmation/${data.id}`)
    } catch (error) {
      console.error('Error creating booking:', error)
      toast.error('Failed to create booking. Please try again.')
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <CustomerLoader />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white px-4 py-4 sticky top-0 z-50 shadow-sm">
        <div className="flex items-center gap-3">
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate(-1)}
            className="w-10 h-10 flex items-center justify-center"
          >
            <ArrowLeftIcon className="w-6 h-6 text-gray-700" />
          </motion.button>
          <div>
            <h1 className="text-lg font-bold text-gray-900">Book table</h1>
            <p className="text-sm text-gray-600">{restaurant?.restaurant_name}</p>
          </div>
        </div>
      </div>

      <div className="px-4 py-6 space-y-6 pb-32">
        {/* Number of Guests */}
        <div className="bg-white rounded-2xl p-4">
          <h2 className="text-base font-bold text-gray-900 mb-4">Number of guest(s)</h2>
          <div className="flex gap-2 overflow-x-auto scrollbar-hide">
            {[1, 2, 3, 4, 5, 6].map((num) => (
              <motion.button
                key={num}
                whileTap={{ scale: 0.95 }}
                onClick={() => setNumberOfGuests(num)}
                className={`flex-shrink-0 w-12 h-12 rounded-xl font-semibold text-base transition-all ${
                  numberOfGuests === num
                    ? 'bg-green-50 border-2 border-green-600 text-green-700'
                    : 'bg-white text-gray-700 border-2 border-gray-200'
                }`}
              >
                {num}
              </motion.button>
            ))}
          </div>
        </div>

        {/* Date Selection */}
        <div className="bg-white rounded-2xl p-4">
          <h2 className="text-base font-bold text-gray-900 mb-4">When are you visiting?</h2>
          <div className="flex gap-2 overflow-x-auto scrollbar-hide mb-4">
            {dates.map((date, index) => (
              <motion.button
                key={index}
                whileTap={{ scale: 0.95 }}
                onClick={() => setSelectedDate(date)}
                className={`flex-shrink-0 rounded-xl p-3 min-w-[80px] transition-all ${
                  selectedDate?.dayNum === date.dayNum
                    ? 'bg-green-50 border-2 border-green-600'
                    : 'bg-white border-2 border-gray-200'
                }`}
              >
                <div className={`text-xs font-medium mb-1 ${
                  selectedDate?.dayNum === date.dayNum ? 'text-green-700' : 'text-gray-600'
                }`}>
                  {date.isToday ? 'Today' : date.day}
                </div>
                <div className={`text-base font-bold mb-1 ${
                  selectedDate?.dayNum === date.dayNum ? 'text-green-700' : 'text-gray-900'
                }`}>
                  {date.dayNum} {date.month}
                </div>
                <div 
                  className="text-xs font-bold px-2 py-0.5 rounded"
                  style={{ backgroundColor: ACTION_GREEN, color: 'white' }}
                >
                  {date.discount}
                </div>
              </motion.button>
            ))}
          </div>

          <p className="text-xs text-gray-600 mb-4">Select the time of day to see the offers</p>

          {/* Meal Periods */}
          <div className="space-y-3">
            {availableMealPeriods.map((periodKey) => {
              const period = allMealPeriods[periodKey]
              const isExpanded = expandedMealPeriod === periodKey
              const isSelected = selectedMealPeriod === periodKey
              const periodSlots = getFilteredTimeSlots(periodKey)

              return (
                <div key={periodKey}>
                  {/* Meal Period Header */}
                  <motion.button
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      setSelectedMealPeriod(periodKey)
                      setExpandedMealPeriod(isExpanded ? null : periodKey)
                      setSelectedTime(null)
                      setShowAllSlots(false)
                    }}
                    className={`w-full flex items-center justify-between p-3 rounded-xl transition-all ${
                      isSelected ? 'bg-green-50 border-2 border-green-600' : 'bg-gray-50 border-2 border-gray-200'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{period.icon}</span>
                      <div className="text-left">
                        <p className={`text-sm font-bold ${isSelected ? 'text-green-700' : 'text-gray-900'}`}>
                          {period.name}
                        </p>
                        <p className="text-xs text-gray-600">{period.timeRange}</p>
                      </div>
                    </div>
                    {isExpanded ? (
                      <ChevronUpIcon className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDownIcon className="w-5 h-5 text-gray-400" />
                    )}
                  </motion.button>

                  {/* Time Slots - Show when expanded */}
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <div className="mt-3 grid grid-cols-3 gap-2">
                          {(showAllSlots ? periodSlots : periodSlots.slice(0, 9)).map((slot, index) => (
                            <motion.button
                              key={index}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => slot.available && setSelectedTime(slot)}
                              disabled={!slot.available}
                              className={`p-3 rounded-xl transition-all ${
                                selectedTime?.time === slot.time
                                  ? 'bg-green-50 border-2 border-green-600'
                                  : slot.available
                                  ? 'bg-white border-2 border-gray-200 hover:border-gray-300'
                                  : 'bg-gray-100 border-2 border-gray-200 opacity-50 cursor-not-allowed'
                              }`}
                            >
                              <div className={`text-sm font-bold mb-1 ${
                                selectedTime?.time === slot.time ? 'text-green-700' : 'text-gray-900'
                              }`}>
                                {slot.time}
                              </div>
                              <div className="text-xs font-semibold text-green-600">
                                {slot.discount}
                              </div>
                            </motion.button>
                          ))}
                        </div>

                        {/* View All Slots */}
                        {periodSlots.length > 9 && (
                          <button
                            onClick={() => setShowAllSlots(!showAllSlots)}
                            className="w-full mt-3 py-2 text-sm font-bold text-green-600 hover:text-green-700"
                          >
                            {showAllSlots ? 'Show less ∧' : 'View all slots ∨'}
                          </button>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {/* Fixed Bottom Button */}
      <div className="fixed bottom-0 left-0 right-0 bg-white p-4 shadow-lg">
        <motion.button
          whileTap={{ scale: 0.98 }}
          onClick={handleProceed}
          disabled={!selectedDate || !selectedTime}
          className={`w-full py-4 rounded-xl font-bold text-base text-white transition-all ${
            selectedDate && selectedTime
              ? 'opacity-100'
              : 'opacity-50 cursor-not-allowed'
          }`}
          style={{ backgroundColor: selectedDate && selectedTime ? ACTION_GREEN : '#9CA3AF' }}
        >
          Proceed
        </motion.button>
      </div>
    </div>
  )
}

export default TableBooking
